import React from 'react';
import {
  Box,
  Container,
  Typography,
  Divider,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';

interface SectionProps {
  title?: string;
  subtitle?: string;
  variant?: 'default' | 'elevated' | 'outlined' | 'transparent';
  color?: string;
  background?: string;
  padding?: 'none' | 'small' | 'medium' | 'large';
  spacing?: 'none' | 'small' | 'medium' | 'large';
  divider?: boolean;
  fullWidth?: boolean;
  centerContent?: boolean;
  id?: string;
  className?: string;
  children: React.ReactNode;
}

const paddingSizes = {
  none: 0,
  small: 2,
  medium: 4,
  large: 6,
};

const spacingSizes = {
  none: 0,
  small: 1,
  medium: 2,
  large: 3,
};

/**
 * Section component for organizing content into distinct areas
 * Provides flexible styling and layout options for MDX content
 */
export const Section: React.FC<SectionProps> = ({
  title,
  subtitle,
  variant = 'default',
  color,
  background,
  padding = 'medium',
  spacing = 'medium',
  divider = false,
  fullWidth = false,
  centerContent = false,
  id,
  className,
  children,
}) => {
  const theme = useTheme();

  const getBackgroundColor = () => {
    if (background) return background;
    if (variant === 'elevated') return theme.palette.background.paper;
    if (variant === 'transparent') return 'transparent';
    return theme.palette.background.default;
  };

  const content = (
    <>
      {(title || subtitle) && (
        <Box sx={{ mb: spacingSizes[spacing] }}>
          {title && (
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                color: color || 'text.primary',
                fontWeight: 600,
                textAlign: centerContent ? 'center' : 'left',
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                textAlign: centerContent ? 'center' : 'left',
                maxWidth: centerContent ? '60ch' : 'none',
                mx: centerContent ? 'auto' : 0,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      {divider && title && (
        <Divider sx={{ mb: spacingSizes[spacing], opacity: 0.6 }} />
      )}

      <Box
        sx={{
          textAlign: centerContent ? 'center' : 'inherit',
        }}
      >
        {children}
      </Box>
    </>
  );

  const sectionStyles = {
    py: paddingSizes[padding],
    px: fullWidth ? 0 : paddingSizes[padding],
    backgroundColor: getBackgroundColor(),
    borderRadius: variant === 'elevated' ? 1 : 0,
    boxShadow: variant === 'elevated' ? 1 : 'none',
    border: variant === 'outlined' ? 1 : 0,
    borderColor: variant === 'outlined' ? 'divider' : 'transparent',
  };

  if (variant === 'elevated' || variant === 'outlined') {
    return (
      <Paper
        id={id}
        className={className}
        sx={sectionStyles}
        elevation={variant === 'elevated' ? 1 : 0}
      >
        {content}
      </Paper>
    );
  }

  return (
    <Box
      id={id}
      className={className}
      component="section"
      sx={sectionStyles}
    >
      {fullWidth ? (
        content
      ) : (
        <Container maxWidth="lg">
          {content}
        </Container>
      )}
    </Box>
  );
};

export default Section;